import React, { useEffect, useState, useMemo } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { fetchSptCircuits, SptCircuit, submitSptCircuitChangeRequest } from '../utils/siteUtils';
import { useAuth } from '../contexts/AuthContext';
import MapBackground from './MapBackground';

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// Styled components
const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  min-height: var(--app-height, 100vh);
  width: 100%;
  padding: 20px;
  box-sizing: border-box;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const Card = styled.div`
  background: rgba(20, 20, 20, 0.92);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 0;
  width: 95%;
  max-width: 540px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.06);
  z-index: 10;
  animation: ${fadeIn} 0.5s ease;
  overflow: hidden;

  @media (max-width: 480px) {
    max-height: 90vh;
  }
`;

const Header = styled.div`
  text-align: center;
  padding: 24px 28px 0;
  animation: ${fadeInUp} 0.6s ease;
`;

const PageIcon = styled.div`
  font-size: 32px;
  margin-bottom: 8px;
`;

const Title = styled.h1`
  color: white;
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 4px;
  letter-spacing: -0.3px;

  @media (max-width: 480px) {
    font-size: 18px;
  }
`;

const Subtitle = styled.div`
  color: rgba(255, 255, 255, 0.4);
  font-size: 13px;
  margin: 0;
`;

const SearchWrapper = styled.div`
  padding: 14px 28px 8px;
  position: relative;
  animation: ${fadeInUp} 0.4s ease 0.1s both;

  @media (max-width: 480px) {
    padding: 12px 20px 8px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 11px 14px 11px 38px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.04);
  color: white;
  font-size: 14px;
  outline: none;
  transition: all 0.2s;
  box-sizing: border-box;

  &::placeholder {
    color: rgba(255, 255, 255, 0.25);
  }

  &:focus {
    border-color: rgba(30, 136, 229, 0.5);
    background: rgba(255, 255, 255, 0.06);
    box-shadow: 0 0 0 3px rgba(30, 136, 229, 0.12);
  }
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 42px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.3);
  font-size: 14px;
  pointer-events: none;

  @media (max-width: 480px) {
    left: 34px;
  }
`;

const FilterRow = styled.div`
  display: flex;
  gap: 6px;
  padding: 0 28px 10px;
  flex-wrap: wrap;
  animation: ${fadeInUp} 0.4s ease 0.15s both;

  @media (max-width: 480px) {
    padding: 0 20px 8px;
  }
`;

const FilterChip = styled.button<{ active: boolean; chipColor: string }>`
  padding: 5px 12px;
  border-radius: 16px;
  border: 1px solid ${props => props.active ? props.chipColor : 'rgba(255, 255, 255, 0.12)'};
  background: ${props => props.active ? `${props.chipColor}22` : 'transparent'};
  color: ${props => props.active ? props.chipColor : 'rgba(255, 255, 255, 0.5)'};
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  text-transform: uppercase;
  letter-spacing: 0.3px;

  &:hover {
    background: ${props => `${props.chipColor}15`};
    border-color: ${props => props.chipColor};
  }
`;

const StatsRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  padding: 0 28px 10px;
  animation: ${fadeInUp} 0.4s ease 0.2s both;
`;

const StatBadge = styled.div<{ color: string }>`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
`;

const StatDot = styled.span<{ color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.color};
  display: inline-block;
`;

const StatCount = styled.span`
  font-weight: 700;
  color: rgba(255, 255, 255, 0.8);
`;

const ListContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 16px 16px;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
  }

  @media (max-width: 480px) {
    padding: 0 12px 12px;
  }
`;

const CircuitItem = styled.div<{ statusColor: string }>`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-left: 3px solid ${props => props.statusColor};
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 8px;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.1);
    border-left-color: ${props => props.statusColor};
  }
`;

const CircuitHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
`;

const CircuitName = styled.div`
  color: white;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.2px;
`;

const StatusBadge = styled.span<{ bgColor: string }>`
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => props.bgColor};
  color: white;
`;

const TypeBadge = styled.span`
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.5);
  margin-left: 6px;
`;

const CircuitDetails = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 16px;
  margin-top: 8px;
`;

const ExpandedContent = styled.div`
  margin-top: 10px;
`;

const AllocationColumns = styled.div`
  display: flex;
  gap: 0;
`;

const AllocationBlock = styled.div`
  flex: 1;
  padding: 0 4px;
`;

const AllocationDivider = styled.div`
  width: 1px;
  background: rgba(255, 255, 255, 0.1);
  margin: 0 8px;
  align-self: stretch;
`;

const AllocationTitle = styled.div`
  color: rgba(255, 255, 255, 0.5);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
`;

const AllocationRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 3px;
`;

const AllocationLabel = styled.span`
  color: rgba(255, 255, 255, 0.35);
  font-size: 11px;
  font-weight: 500;
`;

const AllocationValue = styled.span`
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
  font-weight: 700;
  font-family: 'SF Mono', 'Consolas', monospace;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DetailLabel = styled.span`
  color: rgba(255, 255, 255, 0.35);
  font-size: 11px;
  font-weight: 500;
`;

const DetailValue = styled.span`
  color: rgba(255, 255, 255, 0.7);
  font-size: 11px;
  font-weight: 600;
  font-family: 'SF Mono', 'Consolas', monospace;
`;

const FullWidthRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  grid-column: 1 / -1;
  margin-top: 2px;
`;

const TieCableRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  grid-column: 1 / -1;
  margin-top: 2px;
  padding-top: 4px;
  border-top: 1px solid rgba(255, 255, 255, 0.04);
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  gap: 16px;
`;

const Spinner = styled.div`
  width: 28px;
  height: 28px;
  border: 3px solid rgba(30, 136, 229, 0.2);
  border-top-color: #1e88e5;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const LoadingText = styled.div`
  color: rgba(255, 255, 255, 0.4);
  font-size: 14px;
`;

const ErrorText = styled.div`
  color: #ff6b6b;
  text-align: center;
  padding: 40px 20px;
  font-size: 14px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
`;

const EmptyIcon = styled.div`
  font-size: 36px;
  margin-bottom: 12px;
`;

const EmptyText = styled.div`
  color: rgba(255, 255, 255, 0.5);
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 4px;
`;

const EmptyHint = styled.div`
  color: rgba(255, 255, 255, 0.3);
  font-size: 13px;
`;

const NotesSection = styled.div`
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.04);
  grid-column: 1 / -1;
`;

const NotesText = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  margin-top: 4px;
  line-height: 1.4;
  font-style: italic;
`;

const SuggestChangeButton = styled.button`
  width: 100%;
  margin-top: 10px;
  padding: 8px;
  border-radius: 8px;
  border: 1px solid rgba(30, 136, 229, 0.3);
  background: rgba(30, 136, 229, 0.1);
  color: #64b5f6;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(30, 136, 229, 0.2);
    border-color: rgba(30, 136, 229, 0.5);
  }
`;

const ChangeFormContainer = styled.div`
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const ChangeFormTitle = styled.div`
  color: #64b5f6;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const ChangeFormLabel = styled.div`
  color: rgba(255, 255, 255, 0.4);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 4px;
  margin-top: 8px;
`;

const ChangeFormRow = styled.div`
  display: flex;
  gap: 8px;
`;

const ChangeFormGroup = styled.div`
  flex: 1;
`;

const ChangeFormInput = styled.input`
  width: 100%;
  padding: 7px 10px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.04);
  color: white;
  font-size: 12px;
  outline: none;
  box-sizing: border-box;
  font-family: 'SF Mono', 'Consolas', monospace;
  transition: all 0.2s;

  &:focus {
    border-color: rgba(30, 136, 229, 0.4);
    background: rgba(255, 255, 255, 0.06);
  }
`;

const StatusSelect = styled.select`
  width: 100%;
  padding: 7px 10px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.04);
  color: white;
  font-size: 12px;
  outline: none;
  box-sizing: border-box;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    border-color: rgba(30, 136, 229, 0.4);
  }

  option {
    background: #1a1a1a;
    color: white;
  }
`;

const ChangeFormTextarea = styled.textarea`
  width: 100%;
  padding: 7px 10px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.04);
  color: white;
  font-size: 12px;
  outline: none;
  box-sizing: border-box;
  resize: vertical;
  font-family: inherit;
  transition: all 0.2s;

  &::placeholder {
    color: rgba(255, 255, 255, 0.2);
  }

  &:focus {
    border-color: rgba(30, 136, 229, 0.4);
    background: rgba(255, 255, 255, 0.06);
  }
`;

const ChangeFormButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`;

const ChangeFormCancelBtn = styled.button`
  flex: 1;
  padding: 8px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
`;

const ChangeFormSubmitBtn = styled.button<{ disabled?: boolean }>`
  flex: 1;
  padding: 8px;
  border-radius: 8px;
  border: 1px solid rgba(30, 136, 229, 0.4);
  background: rgba(30, 136, 229, 0.2);
  color: #64b5f6;
  font-size: 12px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: rgba(30, 136, 229, 0.3);
  }
`;

const SubmitMessage = styled.div<{ type: 'success' | 'error' }>`
  margin-top: 8px;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  background: ${props => props.type === 'success' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(244, 67, 54, 0.15)'};
  color: ${props => props.type === 'success' ? '#4caf50' : '#f44336'};
  border: 1px solid ${props => props.type === 'success' ? 'rgba(76, 175, 80, 0.3)' : 'rgba(244, 67, 54, 0.3)'};
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: white;
  padding: 14px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  text-align: center;
  transition: all 0.2s;
  flex-shrink: 0;
  border-radius: 0 0 20px 20px;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

// Helper functions
const getStatusColor = (status: string) => {
  switch (status) {
    case 'faulty': return '#ff4444';
    case 'fixed': return '#4caf50';
    default: return 'rgba(255, 255, 255, 0.2)';
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'faulty': return 'Faulty';
    case 'fixed': return 'Fixed';
    default: return 'OK';
  }
};

const getTypeLabel = (type: string) => {
  switch (type) {
    case 'spt': return 'SPT';
    case 'level_crossing': return 'LC';
    case 'subscriber': return 'Sub';
    case 'signal_box': return 'SB';
    case 'panel': return 'Panel';
    case 'relay_room': return 'RR';
    case 'ground_frame': return 'GF';
    case 'power_signal_box': return 'PSB';
    case 'crossing': return 'Xing';
    default: return 'Circuit';
  }
};

// ExpandableItem component
const ExpandableCircuit: React.FC<{ circuit: SptCircuit; userEmail: string; expanded: boolean; onToggle: () => void }> = ({ circuit, userEmail, expanded, onToggle }) => {
  const [showChangeForm, setShowChangeForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [formData, setFormData] = useState({
    suggested_status: circuit.status,
    suggested_hipath_vert: circuit.hipath_vert || '',
    suggested_hipath_row: circuit.hipath_row?.toString() || '',
    suggested_hipath_pair: circuit.hipath_pair?.toString() || '',
    suggested_cable_pair_no: circuit.cable_pair_no || '',
    suggested_cable_vert: circuit.cable_vert || '',
    suggested_cable_row: circuit.cable_row?.toString() || '',
    suggested_cable_pair: circuit.cable_pair?.toString() || '',
    suggested_tie_cable: circuit.tie_cable || '',
    suggested_notes: circuit.notes || '',
    change_notes: '',
  });

  const statusColor = getStatusColor(circuit.status);

  const handleSubmitChange = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setSubmitting(true);
    setSubmitStatus(null);
    try {
      await submitSptCircuitChangeRequest({
        circuit_id: circuit.id,
        circuit_name: circuit.circuit_name,
        user_email: userEmail,
        suggested_status: formData.suggested_status,
        suggested_hipath_vert: formData.suggested_hipath_vert || undefined,
        suggested_hipath_row: formData.suggested_hipath_row ? parseInt(formData.suggested_hipath_row) : undefined,
        suggested_hipath_pair: formData.suggested_hipath_pair ? parseInt(formData.suggested_hipath_pair) : undefined,
        suggested_cable_pair_no: formData.suggested_cable_pair_no || undefined,
        suggested_cable_vert: formData.suggested_cable_vert || undefined,
        suggested_cable_row: formData.suggested_cable_row ? parseInt(formData.suggested_cable_row) : undefined,
        suggested_cable_pair: formData.suggested_cable_pair ? parseInt(formData.suggested_cable_pair) : undefined,
        suggested_tie_cable: formData.suggested_tie_cable || undefined,
        suggested_notes: formData.suggested_notes || undefined,
        change_notes: formData.change_notes || undefined,
        status: 'pending',
      });
      setSubmitStatus({ message: 'Change request submitted!', type: 'success' });
      setTimeout(() => { setShowChangeForm(false); setSubmitStatus(null); }, 1500);
    } catch (err: any) {
      setSubmitStatus({ message: err.message || 'Failed to submit', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CircuitItem statusColor={statusColor} onClick={() => !showChangeForm && onToggle()}>
      <CircuitHeader>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <CircuitName>{circuit.circuit_name}</CircuitName>
          <TypeBadge>{getTypeLabel(circuit.circuit_type)}</TypeBadge>
        </div>
        <StatusBadge bgColor={statusColor}>
          {getStatusLabel(circuit.status)}
        </StatusBadge>
      </CircuitHeader>

      {expanded && !showChangeForm && (
        <ExpandedContent>
          <AllocationColumns>
            <AllocationBlock>
              <AllocationTitle>HiPath Allocation</AllocationTitle>
              <AllocationRow>
                <AllocationLabel>Vert:</AllocationLabel>
                <AllocationValue>{circuit.hipath_vert || '-'}</AllocationValue>
              </AllocationRow>
              <AllocationRow>
                <AllocationLabel>Row:</AllocationLabel>
                <AllocationValue>{circuit.hipath_row ?? '-'}</AllocationValue>
              </AllocationRow>
              <AllocationRow>
                <AllocationLabel>Pair:</AllocationLabel>
                <AllocationValue>{circuit.hipath_pair ?? '-'}</AllocationValue>
              </AllocationRow>
            </AllocationBlock>
            <AllocationDivider />
            <AllocationBlock>
              <AllocationTitle>Cable Allocation</AllocationTitle>
              <AllocationRow>
                <AllocationLabel>Vert:</AllocationLabel>
                <AllocationValue>{circuit.cable_vert || '-'}</AllocationValue>
              </AllocationRow>
              <AllocationRow>
                <AllocationLabel>Row:</AllocationLabel>
                <AllocationValue>{circuit.cable_row ?? '-'}</AllocationValue>
              </AllocationRow>
              <AllocationRow>
                <AllocationLabel>Pair:</AllocationLabel>
                <AllocationValue>{circuit.cable_pair ?? '-'}</AllocationValue>
              </AllocationRow>
            </AllocationBlock>
          </AllocationColumns>
          {circuit.tie_cable && (
            <FullWidthRow style={{ marginTop: 8 }}>
              <DetailLabel>Tie Cable</DetailLabel>
              <DetailValue>{circuit.tie_cable}</DetailValue>
            </FullWidthRow>
          )}
          <FullWidthRow>
            <DetailLabel>Pair No</DetailLabel>
            <DetailValue>{circuit.cable_pair_no || '-'}</DetailValue>
          </FullWidthRow>
          {circuit.notes && (
            <NotesSection>
              <DetailLabel>Additional Notes</DetailLabel>
              <NotesText>{circuit.notes}</NotesText>
            </NotesSection>
          )}
          <SuggestChangeButton onClick={(e) => { e.stopPropagation(); setShowChangeForm(true); }}>
            Suggest a Change
          </SuggestChangeButton>
        </ExpandedContent>
      )}

      {showChangeForm && (
        <ChangeFormContainer onClick={(e) => e.stopPropagation()}>
          <ChangeFormTitle>Suggest a Change — {circuit.circuit_name}</ChangeFormTitle>

          <ChangeFormLabel>Status</ChangeFormLabel>
          <StatusSelect
            value={formData.suggested_status}
            onChange={(e) => setFormData({ ...formData, suggested_status: e.target.value as 'faulty' | 'fixed' | 'not_reported' })}
          >
            <option value="not_reported">OK (Not Reported)</option>
            <option value="faulty">Faulty</option>
            <option value="fixed">Fixed</option>
          </StatusSelect>

          <ChangeFormRow>
            <ChangeFormGroup>
              <ChangeFormLabel>HiPath Vert</ChangeFormLabel>
              <ChangeFormInput value={formData.suggested_hipath_vert} onChange={(e) => setFormData({ ...formData, suggested_hipath_vert: e.target.value })} />
            </ChangeFormGroup>
            <ChangeFormGroup>
              <ChangeFormLabel>HiPath Row</ChangeFormLabel>
              <ChangeFormInput value={formData.suggested_hipath_row} onChange={(e) => setFormData({ ...formData, suggested_hipath_row: e.target.value })} />
            </ChangeFormGroup>
            <ChangeFormGroup>
              <ChangeFormLabel>HiPath Pair</ChangeFormLabel>
              <ChangeFormInput value={formData.suggested_hipath_pair} onChange={(e) => setFormData({ ...formData, suggested_hipath_pair: e.target.value })} />
            </ChangeFormGroup>
          </ChangeFormRow>

          <ChangeFormRow>
            <ChangeFormGroup>
              <ChangeFormLabel>Cable Vert</ChangeFormLabel>
              <ChangeFormInput value={formData.suggested_cable_vert} onChange={(e) => setFormData({ ...formData, suggested_cable_vert: e.target.value })} />
            </ChangeFormGroup>
            <ChangeFormGroup>
              <ChangeFormLabel>Cable Row</ChangeFormLabel>
              <ChangeFormInput value={formData.suggested_cable_row} onChange={(e) => setFormData({ ...formData, suggested_cable_row: e.target.value })} />
            </ChangeFormGroup>
            <ChangeFormGroup>
              <ChangeFormLabel>Cable Pair</ChangeFormLabel>
              <ChangeFormInput value={formData.suggested_cable_pair} onChange={(e) => setFormData({ ...formData, suggested_cable_pair: e.target.value })} />
            </ChangeFormGroup>
          </ChangeFormRow>

          <ChangeFormRow>
            <ChangeFormGroup>
              <ChangeFormLabel>Pair No</ChangeFormLabel>
              <ChangeFormInput value={formData.suggested_cable_pair_no} onChange={(e) => setFormData({ ...formData, suggested_cable_pair_no: e.target.value })} />
            </ChangeFormGroup>
            <ChangeFormGroup style={{ flex: 2 }}>
              <ChangeFormLabel>Tie Cable</ChangeFormLabel>
              <ChangeFormInput value={formData.suggested_tie_cable} onChange={(e) => setFormData({ ...formData, suggested_tie_cable: e.target.value })} />
            </ChangeFormGroup>
          </ChangeFormRow>

          <ChangeFormLabel>Additional Notes</ChangeFormLabel>
          <ChangeFormTextarea
            value={formData.suggested_notes}
            onChange={(e) => setFormData({ ...formData, suggested_notes: e.target.value })}
            placeholder="Notes about this circuit..."
            rows={2}
          />

          <ChangeFormLabel>Reason for Change</ChangeFormLabel>
          <ChangeFormTextarea
            value={formData.change_notes}
            onChange={(e) => setFormData({ ...formData, change_notes: e.target.value })}
            placeholder="Why is this change needed?"
            rows={2}
          />

          {submitStatus && (
            <SubmitMessage type={submitStatus.type}>{submitStatus.message}</SubmitMessage>
          )}

          <ChangeFormButtons>
            <ChangeFormCancelBtn onClick={(e) => { e.stopPropagation(); setShowChangeForm(false); setSubmitStatus(null); }}>
              Cancel
            </ChangeFormCancelBtn>
            <ChangeFormSubmitBtn onClick={handleSubmitChange} disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit Change'}
            </ChangeFormSubmitBtn>
          </ChangeFormButtons>
        </ChangeFormContainer>
      )}
    </CircuitItem>
  );
};

// Main component
interface HiPathAllocationsProps {
  onBack: () => void;
}

const HiPathAllocations: React.FC<HiPathAllocationsProps> = ({ onBack }) => {
  const { user } = useAuth();
  const [circuits, setCircuits] = useState<SptCircuit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [expandedCircuitId, setExpandedCircuitId] = useState<number | null>(null);

  useEffect(() => {
    const loadCircuits = async () => {
      try {
        setLoading(true);
        const data = await fetchSptCircuits();
        setCircuits(data);
      } catch (err: any) {
        console.error('Error fetching circuits:', err);
        setError(err.message || 'Failed to load circuits');
      } finally {
        setLoading(false);
      }
    };
    loadCircuits();
  }, []);

  const filteredCircuits = useMemo(() => {
    let result = circuits;

    // Filter by status
    if (statusFilter) {
      result = result.filter(c => c.status === statusFilter);
    }

    // Filter by search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(c =>
        c.circuit_name.toLowerCase().includes(q) ||
        (c.tie_cable && c.tie_cable.toLowerCase().includes(q)) ||
        c.circuit_type.toLowerCase().includes(q)
      );
    }

    return result;
  }, [circuits, searchQuery, statusFilter]);

  // Stats
  const stats = useMemo(() => {
    const s = { faulty: 0, fixed: 0, not_reported: 0 };
    circuits.forEach(c => { s[c.status]++; });
    return s;
  }, [circuits]);

  return (
    <>
      <MapBackground />
      <Container>
        <Card>
          <Header>
            <PageIcon>🚦</PageIcon>
            <Title>Doncaster HiPath Allocations</Title>
            <Subtitle>
              {loading ? 'Loading...' : `${circuits.length} circuits documented`}
            </Subtitle>
          </Header>

          {!loading && !error && (
            <>
              <StatsRow>
                <StatBadge color="#ff4444">
                  <StatDot color="#ff4444" />
                  Faulty: <StatCount>{stats.faulty}</StatCount>
                </StatBadge>
                <StatBadge color="#4caf50">
                  <StatDot color="#4caf50" />
                  Fixed: <StatCount>{stats.fixed}</StatCount>
                </StatBadge>
                <StatBadge color="rgba(255,255,255,0.3)">
                  <StatDot color="rgba(255,255,255,0.3)" />
                  OK: <StatCount>{stats.not_reported}</StatCount>
                </StatBadge>
              </StatsRow>

              <FilterRow>
                <FilterChip
                  active={statusFilter === null}
                  chipColor="#1e88e5"
                  onClick={() => setStatusFilter(null)}
                >
                  All ({circuits.length})
                </FilterChip>
                <FilterChip
                  active={statusFilter === 'faulty'}
                  chipColor="#ff4444"
                  onClick={() => setStatusFilter(statusFilter === 'faulty' ? null : 'faulty')}
                >
                  Faulty ({stats.faulty})
                </FilterChip>
                <FilterChip
                  active={statusFilter === 'fixed'}
                  chipColor="#4caf50"
                  onClick={() => setStatusFilter(statusFilter === 'fixed' ? null : 'fixed')}
                >
                  Fixed ({stats.fixed})
                </FilterChip>
                <FilterChip
                  active={statusFilter === 'not_reported'}
                  chipColor="rgba(255,255,255,0.5)"
                  onClick={() => setStatusFilter(statusFilter === 'not_reported' ? null : 'not_reported')}
                >
                  OK ({stats.not_reported})
                </FilterChip>
              </FilterRow>
            </>
          )}

          <SearchWrapper>
            <SearchIcon>🔍</SearchIcon>
            <SearchInput
              type="text"
              placeholder="Search circuits..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </SearchWrapper>

          <ListContainer>
            {loading ? (
              <LoadingContainer>
                <Spinner />
                <LoadingText>Loading circuits...</LoadingText>
              </LoadingContainer>
            ) : error ? (
              <ErrorText>{error}</ErrorText>
            ) : filteredCircuits.length === 0 ? (
              <EmptyState>
                <EmptyIcon>🔍</EmptyIcon>
                <EmptyText>No circuits found</EmptyText>
                <EmptyHint>Try a different search or filter</EmptyHint>
              </EmptyState>
            ) : (
              filteredCircuits.map(circuit => (
                <ExpandableCircuit
                  key={circuit.id}
                  circuit={circuit}
                  userEmail={user?.email || ''}
                  expanded={expandedCircuitId === circuit.id}
                  onToggle={() => setExpandedCircuitId(expandedCircuitId === circuit.id ? null : circuit.id)}
                />
              ))
            )}
          </ListContainer>

          <BackButton onClick={onBack}>
            Back to Resources
          </BackButton>
        </Card>
      </Container>
    </>
  );
};

export default HiPathAllocations;
